/** @jsx jsx */

import { LegacyButton } from '@zapier/design-system';
import { jsx } from '@emotion/core';

import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { useAvroEvent, usePrivatePlatform } from 'app/developer-v3/hooks';
import {
  TABS as SIDEBAR_RIGHT_TABS,
  useSidebarRight,
} from 'app/developer-v3/components/SidebarRight';

import * as CTAs from './CTAs';

type Props = {
  hasAuthentication: boolean;
  hasValidated: boolean;
  isPrivate: boolean;
  ordinal: number;
};

function CTAVerification({
  hasAuthentication,
  hasValidated,
  isPrivate,
  ordinal,
}: Props) {
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  const { isPublicIntentionElementVisible } = usePrivatePlatform();

  const { update: updateSidebarRight } = useSidebarRight();
  const openSidebarRightForValidation = () =>
    updateSidebarRight({ tabActive: SIDEBAR_RIGHT_TABS.VALIDATE });

  return (
    <CTAs.Lockable
      action={
        <LegacyButton
          aria-label={hasValidated ? 'See Results' : 'Run Validation'}
          color={hasValidated ? 'secondary' : 'primary'}
          onClick={() => {
            if (hasValidated) {
              emitPartnerUserInteractionEvent({
                eventAction: 'click',
                interactionGoal: 'See validation results',
                interactionName: 'integration_home_button_clicked',
                interactionRawText: 'See Results',
                optionSelected: 'validation',
              });
            } else {
              emitPartnerUserInteractionEvent({
                eventAction: 'click',
                interactionGoal: 'Run validation',
                interactionName: 'integration_home_button_clicked',
                interactionRawText: 'Run Validation',
                optionSelected: 'validation',
              });
            }
            openSidebarRightForValidation();
          }}
        >
          {hasValidated ? 'See Results' : 'Run Validation'}
        </LegacyButton>
      }
      description={
        isPrivate && !isPublicIntentionElementVisible
          ? 'Run automated checks to identify errors in your integration version. We provide user experience recommendations to help you improve your integration.'
          : 'Check your project for errors, get specific user experience recommendations, and get a list of remaining items that are required before you can make your integration version available to everyone on Zapier.'
      }
      icon="alertCircleFill"
      isActive={hasAuthentication && !hasValidated}
      learnMoreUrl={`${BASE_DOC_URL_VB}/publish/integration-checks-reference`}
      ordinal={ordinal}
      title="Validation"
    />
  );
}

export default CTAVerification;
